import {
  notificationTypes,
  referenceableTypes,
} from '@/components/global/HeaderMenu/HeaderMenuNotifications/constants';
import type { RawLocation } from 'vue-router';
import type { Comment, JobsHiring, Notification, Post, Rating, Testimonial, User } from '@/types/graphql';
import { PUBLIC_PORTFOLIO_CONNECTIONS_IMPORT_MODAL, REFERRAL_HIRED_MODAL } from '~/constants/dynamicModals';
import { JOBS_OPPORTUNITIES_OPPORTUNITYSLUG_PAGE, PORTFOLIO_PAGE } from '~/constants/pages';
import { getTestimonialApprovalRoute } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/utils';

const getPostRoute = (post: Post): RawLocation => ({
  name: 'war-room-posts-postSlug',
  params: { postSlug: post.slug },
});
const getCommentRoute = (comment: Comment): RawLocation => ({
  name: 'war-room-posts-postSlug-comments-commentId',
  params: { postSlug: comment.post.slug, commentId: comment.id.toString() },
});
const getTestimonialRoute = (testimonial: Testimonial): RawLocation => {
  return getTestimonialApprovalRoute(testimonial.requestor.slug, testimonial.id);
};
const getReferralRoute = (notificationType: string): RawLocation => ({
  query: {
    dynamic_modal: REFERRAL_HIRED_MODAL,
    notificationType,
  },
});
const getConnectionsRoute = (user: User): RawLocation => ({
  name: PORTFOLIO_PAGE.name,
  params: { slug: user.slug! },
  query: {
    dynamic_modal: PUBLIC_PORTFOLIO_CONNECTIONS_IMPORT_MODAL,
  },
});

const getReactableRoute = (referenceable: Rating): RawLocation => {
  switch (referenceable.reactable.__typename) {
    case referenceableTypes.POST:
      return getPostRoute(referenceable.reactable as Post);
    case referenceableTypes.COMMENT:
      return getCommentRoute(referenceable.reactable as Comment);
    default:
      return getPostRoute(referenceable.reactable as Post);
  }
};

const getJobIntroductionRoute = (hiring: JobsHiring): RawLocation => {
  return {
    name: JOBS_OPPORTUNITIES_OPPORTUNITYSLUG_PAGE.name,
    params: { opportunitySlug: hiring.hashid },
  };
};

export const getRoute = (notification: Notification): RawLocation => {
  const commentTypes = [
    notificationTypes.REPLY_TO_COMMENT,
    notificationTypes.MENTION_IN_COMMENT,
    notificationTypes.COMMENT,
  ];
  const reactableTypes = [notificationTypes.REACTION_TO_COMMENT, notificationTypes.REACTION_TO_POST];

  if (notificationTypes.TESTIMONIAL === notification.type) {
    return getTestimonialRoute(notification.referenceable as Testimonial);
  } else if (commentTypes.includes(notification.type)) {
    return getCommentRoute(notification.referenceable as Comment);
  } else if (reactableTypes.includes(notification.type)) {
    return getReactableRoute(notification.referenceable as Rating);
  } else if (notificationTypes.CONTACTS_REMINDER === notification.type) {
    return getConnectionsRoute(notification.referenceable as User);
  } else if ([notificationTypes.REFERRALHIRED, notificationTypes.REFERRAL].includes(notification.type)) {
    return getReferralRoute(notification.type);
  } else if (notificationTypes.JOB_INTRODUCTION === notification.type) {
    return getJobIntroductionRoute(notification.referenceable as JobsHiring);
  } else {
    return getPostRoute(notification.referenceable as Post);
  }
};
